// Phone chrome keeps navigation local. App destinations still use the shell's
// permission catalog and guarded opener. No navigation path bypasses either.
export function phoneNavigationAvailable(root) {
  const r = root || (this.state && this.state.root)
  if (!r || !r.workspace) return false
  const path = this.call('stripWorkspacePrefix', r.currentPath || '/').split(/[?#]/)[0]
  const fns = (this.context && this.context.functions) || {}
  if (typeof fns.moduleImmersiveActive === 'function' && this.call('moduleImmersiveActive', r))
    return false
  return !this.call('isChromelessShellPath', path) && path !== '/meet-guest'
}

export function phoneNavigationSync(el, s) {
  const host = el || this
  const root = (s && s.root) || (host.state && host.state.root)
  if (!root || typeof root.update !== 'function') return
  const workspace = root.workspace && (root.workspace.id || root.workspace._id)
  const route = String(root.currentPath || '/').split(/[?#]/)[0]
  const key = JSON.stringify([workspace, route, root._canvasSlot, root._canvasSlotSecond])
  const patch = {}
  if (host.__phoneNavLocation && host.__phoneNavLocation !== key) {
    patch._phoneNavPanel = null
    patch._phoneNavError = null
    patch._phoneNavOperation = null
    patch._phoneComposerOpen = false
    for (const rail of [
      '_filesRailOpen',
      '_canvasRailOpen',
      '_chatSidebarOpen',
      '_calRailOpen',
      '_meetRailOpen',
      '_ticketsRailOpen',
      '_docsRailOpen',
      '_mailRailOpen',
      '_moduleRailOpen'
    ])
      if (root[rail]) patch[rail] = false
  }
  host.__phoneNavLocation = key
  const media = host.context && host.context.designSystem && host.context.designSystem.media
  const query = media && media.mobileL
  if (typeof query === 'string' && typeof window !== 'undefined' && window.matchMedia) {
    const phone = window.matchMedia(query.startsWith('(') ? query : '(' + query + ')').matches
    if (root._isPhoneViewport !== phone) patch._isPhoneViewport = phone
    if (!phone && root._phoneNavPanel) patch._phoneNavPanel = null
  }
  // The route trail the phone Back steps (phoneNavigationBack below): every
  // route the shell shows, in the panes' own history shape (canvas
  // functions/slotHistory.js). A Back landing is the cursor already, so it
  // records nothing — the panes' rule, no guard flag.
  const fns = (host.context && host.context.functions) || {}
  if (typeof fns.histRecord === 'function' && root.currentPath) {
    const hist = host.call('histRecord', root._phoneNavHist, String(root.currentPath))
    if (hist && hist !== root._phoneNavHist) patch._phoneNavHist = hist
  }
  if (Object.keys(patch).length) root.update(patch, { preventFetch: true })
}

// Back on a full-page app route at phone width (Nika 2026-10-31, POB S11c2
// option B): the page before this one, through the router; with no page
// before it, Home. The pane header's Back does the same over its own trail
// (canvas components/PageSlot.js _histBtn).
export function phoneNavigationBack() {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const fns = (this.context && this.context.functions) || {}
  const step = typeof fns.histBack === 'function' ? this.call('histBack', root._phoneNavHist) : null
  if (!step) return this.call('phoneNavigationOpen', '/')
  root.update({ _phoneNavHist: step.hist }, { preventFetch: true })
  this.router(step.path, this.getRoot())
  return step.path
}

export function phoneNavigationPanel(panel) {
  const root = this.state && this.state.root
  if (!this.call('phoneNavigationAvailable', root)) return false
  if (panel !== 'apps' && panel !== 'menu' && panel !== null) return false
  root.update(
    { _phoneNavPanel: panel, _phoneNavError: null, _phoneNavOperation: null },
    { preventFetch: true }
  )
  return true
}

export function phoneNavigationClose() {
  return this.call('phoneNavigationPanel', null)
}

// The launcher's own groups and tiles (searchAppGroupsFor), kept to local
// routes and to the first tile of each route. The sheet renders these through
// the launcher's section and tile; the opener checks against the same list.
export function phoneNavigationAppGroups(root) {
  const r = root || (this.state && this.state.root)
  if (!this.call('phoneNavigationAvailable', r)) return []
  const groups = []
  const seen = new Set()
  for (const group of this.call('searchAppGroupsFor', r) || []) {
    const apps = []
    for (const app of (group && group.apps) || []) {
      const route = app && app.route
      if (typeof route !== 'string' || !/^\/(?!\/)[^\s\\]*$/.test(route) || seen.has(route))
        continue
      seen.add(route)
      apps.push(app)
    }
    if (apps.length) groups.push({ ...group, apps })
  }
  return groups
}

export function phoneNavigationApps(root) {
  const rows = []
  for (const group of this.call('phoneNavigationAppGroups', root)) {
    for (const app of group.apps) {
      rows.push({
        value: app.route,
        label: this.call('polyglot', app.title || app.label || app.route),
        hint: this.call('polyglot', group.label || ''),
        icon: app.icon || 'arrowUpRight'
      })
    }
  }
  return rows
}

export function phoneNavigationOpen(value) {
  const root = this.state && this.state.root
  if (!this.call('phoneNavigationAvailable', root)) return false
  if (value !== '/' && !this.call('phoneNavigationApps', root).some((row) => row.value === value))
    return false
  const operation = crypto.randomUUID()
  const workspace = root.workspace
  root.update({ _phoneNavOperation: operation, _phoneNavError: null }, { preventFetch: true })
  const settle = (ok) => {
    if (root.workspace !== workspace || root._phoneNavOperation !== operation) return false
    root.update(
      {
        _phoneNavOperation: null,
        _phoneNavError: ok === false ? 'navigation' : null,
        _phoneNavPanel: ok === false ? root._phoneNavPanel : null
      },
      { preventFetch: true }
    )
    return ok !== false
  }
  try {
    const opened = this.call('openAppPage', value)
    return opened && typeof opened.then === 'function'
      ? opened.then(settle, () => settle(false))
      : settle(opened)
  } catch (_) {
    return settle(false)
  }
}

export function phoneNavigationPageRail(root) {
  const r = root || (this.state && this.state.root)
  if (!r) return null
  const path = this.call('stripWorkspacePrefix', r.currentPath || '/').split(/[?#]/)[0]
  if (path === '/files') return '_filesRailOpen'
  if (
    ['/infra', '/agents', '/my-agents', '/team-and-agents', '/canvas'].includes(path) ||
    path === '/studio' ||
    path.startsWith('/studio/')
  )
    return '_canvasRailOpen'
  if (path === '/chat') return '_chatSidebarOpen'
  if (path === '/calendar') return '_calRailOpen'
  if (path === '/meet') return '_meetRailOpen'
  if (
    [
      '/tasks',
      '/tasks/board',
      '/tasks/epics',
      '/tickets',
      '/tickets/board',
      '/tickets/epics'
    ].includes(path)
  )
    return '_ticketsRailOpen'
  if (['/notes', '/documents', '/knowledge-base'].includes(path)) return '_docsRailOpen'
  // A module's own navigation (chrome.rail) on its surface routes: the phone
  // keeps it in a drawer (app.js ModuleRailHost) instead of a column.
  const fns = (this.context && this.context.functions) || {}
  if (
    r._projectCanvasOpen &&
    typeof fns.canvasEditorOpen === 'function' &&
    this.call('canvasEditorOpen', r)
  )
    return '_canvasRailOpen'
  if (typeof fns.moduleRailActive === 'function' && this.call('moduleRailActive', r))
    return '_moduleRailOpen'
  return null
}

export function phoneNavigationBrowse() {
  const root = this.state && this.state.root
  const key = this.call('phoneNavigationPageRail', root)
  if (!key || !this.call('phoneNavigationAvailable', root)) return false
  root.update({ _phoneNavPanel: null, [key]: true }, { preventFetch: true })
  return true
}

export function phoneNavigationAssistant(canCompose = true) {
  const root = this.state && this.state.root
  if (
    !this.call('phoneNavigationAvailable', root) ||
    !this.call('featureEnabled', root, 'aiAssistant')
  )
    return false
  if (root.assistantOpen && root.assistantDock !== 'minimized') {
    root.update({ assistantOpen: false, _phoneNavPanel: null }, { preventFetch: true })
    return true
  }
  const path = this.call('stripWorkspacePrefix', root.currentPath || '/').split(/[?#]/)[0]
  // The builder's AI is its own chat (functions/addAppBuilderChat.js): the
  // general drawer here opened a NEW chat, and a build thread picked from its
  // History sent paid workspace turns (persona round 2d, marcus at 390 px).
  // No builder on screen yet → the drawer, as on /chat.
  if (path === '/add-app') {
    this.call('phoneNavigationClose')
    if (this.call('addAppBuilderChatReveal') === true) {
      if (root._phoneComposerOpen)
        root.update({ _phoneComposerOpen: false }, { preventFetch: true })
      return true
    }
    return this.call('assistantThreadToSidebar')
  }
  if (path === '/chat' || canCompose === false) {
    this.call('phoneNavigationClose')
    return this.call('assistantThreadToSidebar')
  }
  const open = !root._phoneComposerOpen
  root.update({ _phoneNavPanel: null, _phoneComposerOpen: open }, { preventFetch: true })
  // The composer's hide factory re-runs in the update's microtask, so a
  // focus now would land on a hidden field. One microtask later it shows,
  // and the focus still runs inside the tap that raised it.
  if (open) queueMicrotask(() => this.call('assistantBoardFocus', 'composer'))
  return true
}

// The unread notifications, counted as the header bell counts them (shared
// NotificationBell). The phone hides that bell (phoneNavigationStyles below),
// so the dock's Menu and the menu's Inbox row carry the count instead
// (components/PhoneNavigation.js).
export function phoneNavigationUnread(root) {
  const r = root || (this.state && this.state.root)
  return ((r && r.notifs) || []).filter((n) => n && !n.read).length
}

export function phoneNavigationSearch() {
  const root = this.state && this.state.root
  if (!this.call('phoneNavigationAvailable', root)) return false
  const shell = this.getRoot()
  const opener = shell.PhoneNavigation?.Dock?.PhoneNavButton_menu?.node
  if (!root._headerSearchOpen && opener) root.__headerSearchReturnFocusTo = opener
  root.update({ _phoneNavPanel: null, _headerSearchOpen: true }, { preventFetch: true })
  const header = shell.AppHeader
  const input =
    header && header.HeaderSearch && header.HeaderSearch.Form && header.HeaderSearch.Form.Input
  if (input && input.node && typeof input.node.focus === 'function')
    input.node.focus({ preventScroll: true })
  return true
}

// One CSS contract for the live shell and the isolated verification frame.
// The dock's live measurement supplies --phone-clear. All other values come
// from the brand's spacing, media and size tokens, or the existing shell vars.
// The dock (or the shown composer) sets the stack's LINE, --stack-line; the
// shell (app.js) derives --slot-bottom from it and from the toast stack's
// reach, once.
// The parked composer is not here: _composerVisible (workspace app.js) hides
// it and keeps <html data-prompt> true, so the rules below read that mirror.
//
// THE HOME ENDS ABOVE THE DOCK, AND SCROLLING TO A CONTROL CLEARS IT (persona
// audit round 1, marcus: "next ask" chips under the tab bar; a tap on one
// opened the Apps sheet). The home's scroller (MainColumn) is as tall as the
// screen, so the dock floats over its last 64px: `padding-bottom` makes the END
// of the scroll clear the dock, and `scroll-padding-bottom` makes every scroll
// TO a control (Tab, focus(), scrollIntoView, the scroll a click driver does
// first) leave it above the dock, not under it. Without it a chip left at an
// offset inside the dock band counted as "in view": focus did not move it and
// the tap landed on the dock. Measured on dev, 390x844: the chip stayed at
// 795-831 under a dock starting at 780 through scrollIntoViewIfNeeded,
// scrollIntoView and focus(); with the rule, nearest ends at 770 (10px above),
// the others at the end of the scroll. Locked by
// __tests__/phoneHomeDockClearance.test.js.
//
// THE OPEN SEARCH SITS UNDER THE SCREEN'S TOP EDGE, WHOLE (Nika 2026-10-27:
// "fix navbar search button click on workspace for both left and top navbar
// modes"). The pill's `transform: none` holds it closed only: the shared
// header's open phone block (AppHeader HeaderSearch `.isSearchOpen`) is emitted
// `!important` and centred the pill on this `top` with a -50% shift. Measured
// on dev 2fc624ff0 at 390px: its top 7.6px sat above the screen. That block
// reads `--hs-open-phone`, so the dock sets it to none. Locked by
// __tests__/phoneSearchPillTopEdge.test.js.
export function phoneNavigationStyles() {
  const ds = this.context && this.context.designSystem
  const media = ds && ds.media && ds.media.mobileL
  if (typeof media !== 'string') return ''
  const query = media.startsWith('(') ? media : '(' + media + ')'
  const phone = 'html:has([data-phone-nav="true"])'
  const content = ' [data-key="smblsapp"] > [data-key="content"]'
  return (
    '@media ' +
    query +
    '{' +
    phone +
    '{--phone-clear:calc(var(--spacing-D) + env(safe-area-inset-bottom));--slot-top:calc(env(safe-area-inset-top) + var(--spacing-Z));--shell-top:var(--slot-top);--chip-row:0;--stack-line:calc(var(--phone-clear) + var(--spacing-Z));--composer-bottom:calc(var(--phone-clear) + var(--spacing-Z));--phone-back-row:calc(var(--size-controlH) + var(--spacing-Z))}' +
    phone +
    ' [data-key="AppHeader"]{height:0;min-height:0;padding:0;background:transparent;box-shadow:none;backdrop-filter:none;-webkit-backdrop-filter:none;pointer-events:none}' +
    phone +
    ' [data-key="AppHeader"] > [data-key="HeaderSearch"]{position:fixed;top:calc(env(safe-area-inset-top) + var(--spacing-Z));left:var(--spacing-A);right:var(--spacing-A);width:auto;max-width:none;transform:none;--hs-open-phone:none}' +
    phone +
    ' [data-key="AppHeader"] > [data-key="HeaderLeft"],' +
    phone +
    ' [data-key="AppHeader"] > [data-key="HeaderRight"],' +
    phone +
    ' [data-key="AppHeader"] > [data-key="HeaderTabs"]{display:none}' +
    phone +
    '[data-prompt="shown"]{--stack-line:calc(var(--prompt-clear) + var(--nav-gap))}' +
    phone +
    ' [data-key="CanvasPromptTextarea"]{margin-bottom:0}' +
    phone +
    // The app-route sheet's foot clears the dock. The mount has no position
    // (app.js), so a dialog in it resolves against the viewport: its frame
    // takes the same bottom, or the sheet's foot (Save / Cancel) runs under
    // the fixed dock (BESSI QA 2026-10-28, 375px: frame 10..900, mount
    // 10..804).
    '[data-shell-route="app"]' +
    content +
    '{margin:var(--slot-top) 0 var(--slot-bottom);height:calc(100dvh - var(--slot-top) - var(--slot-bottom));min-height:0;--app-dialog-inset:var(--slot-top) 0 var(--slot-bottom)}' +
    phone +
    '[data-shell-route="home"]:not([data-customizing="open"]) [data-key="content"] [data-key="MainColumn"]{padding-bottom:calc(var(--slot-bottom) + var(--spacing-A));scroll-padding-bottom:var(--slot-bottom)}' +
    phone +
    ':not([data-slot="open"]) [data-key="ChatMobileSidebarBar"],' +
    phone +
    ':not([data-slot="open"]) [data-key="DocsMobileSidebarBar"],' +
    phone +
    ':not([data-slot="open"]) [data-key="CalRailBtn"],' +
    phone +
    ':not([data-slot="open"]) [data-phone-page-menu]{display:none}' +
    phone +
    ' [data-key="FlyBarActions"]{display:none}' +
    // The phone Back (components/PhoneNavigation.js PhoneBack): shown on a
    // full-page app route with no pane open — a pane has its own Back — and
    // the page sheet starts below its row, so it never covers the app's
    // first row.
    phone +
    '[data-shell-route="app"]:not([data-slot="open"]) [data-phone-back]{display:flex}' +
    phone +
    '[data-shell-route="app"]:not([data-slot="open"])' +
    content +
    '{margin-top:calc(var(--slot-top) + var(--phone-back-row));height:calc(100dvh - var(--slot-top) - var(--phone-back-row) - var(--slot-bottom));--app-dialog-inset:calc(var(--slot-top) + var(--phone-back-row)) 0 var(--slot-bottom)}' +
    phone +
    '[data-slot="open"] [data-phone-dock]{visibility:hidden;pointer-events:none}' +
    '}'
  )
}
